import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Super admin: the reinstatement fee an owner pays to set up a texting number
// again after a release (SMS Phase 6c), from GET and PUT
// /api/admin/sms/settings (platform_settings, migration 107). Entered in
// dollars, whole cents, 0 to 1000.00; 0 charges nothing. "Use the default"
// removes the amount, so SMS_REINSTATEMENT_FEE_CENTS applies again. The
// Settings card and the release email read the same amount.
const API = `${process.env.REACT_APP_API_URL}/api/admin/sms/settings`;
const MAX_CENTS = 100000;

const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

// "25", "25.5", "25.50", "$1,000.00" -> cents; anything else -> null.
export const parseDollars = (text) => {
  const s = String(text || '').trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  return cents <= MAX_CENTS ? cents : null;
};

function AdminSmsFeeSetting({ refreshKey }) {
  const [fee, setFee] = useState(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  // True once the admin types, until a successful save. A refetch (a
  // pull-to-refresh bumps refreshKey) then updates the "Now" line but leaves
  // the input alone, so the amount being typed is not replaced.
  const dirtyRef = useRef(false);

  const show = (f) => {
    setFee(f);
    if (!dirtyRef.current) setValue((f.cents / 100).toFixed(2));
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(API);
      if (res.data && res.data.reinstatementFee) show(res.data.reinstatementFee);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the reinstatement fee');
    }
  }, []);

  // refreshKey: AdminPanel bumps it on pull-to-refresh.
  useEffect(() => { load(); }, [load, refreshKey]);

  const save = async (cents) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await axios.put(API, { reinstatementFeeCents: cents });
      const f = res.data.reinstatementFee;
      dirtyRef.current = false;
      show(f);
      setNotice(cents === null
        ? `Reinstatement fee back to the default, ${dollars(f.cents)}.`
        : `Reinstatement fee set to ${dollars(f.cents)}${f.cents === 0 ? ' (charges nothing)' : ''}.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the reinstatement fee');
    } finally {
      setBusy(false);
    }
  };

  if (!fee && !error) return null;
  const cents = parseDollars(value);

  return (
    <div className="companies-section" data-testid="sms-fee-setting">
      <h3>Reinstatement fee</h3>
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      {fee && (
        <>
          <p className="text-muted" data-testid="sms-fee-source">
            Charged once when an owner sets up a texting number again after a release. Now {dollars(fee.cents)}
            {fee.source === 'app'
              ? `, set here${fee.updatedAt ? ` on ${new Date(fee.updatedAt).toLocaleDateString()}` : ''}. The default is ${dollars(fee.defaultCents)}.`
              : ', the default (SMS_REINSTATEMENT_FEE_CENTS).'}
            {fee.tableMissing && ' Migration 107 is not applied on this database, so it cannot be changed yet.'}
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="sms-reinstatement-fee">Amount ($)</label>
            <input id="sms-reinstatement-fee" className="form-input" style={{ maxWidth: '8rem' }} inputMode="decimal"
              value={value} onChange={(e) => { dirtyRef.current = true; setValue(e.target.value); }} autoComplete="off" disabled={fee.tableMissing} />
            <button type="button" className="btn btn-primary" onClick={() => save(cents)}
              disabled={busy || fee.tableMissing || cents === null || (fee.source === 'app' && cents === fee.cents)}>
              {busy ? 'Saving...' : 'Save'}
            </button>
            {fee.source === 'app' && (
              <button type="button" className="btn btn-secondary" onClick={() => save(null)} disabled={busy}>
                Use the default
              </button>
            )}
          </div>
          <p className="text-muted" style={{ fontSize: '0.8rem' }}>
            {cents === null
              ? 'Enter dollars and cents from 0.00 to 1000.00.'
              : cents === 0 ? '0 charges nothing: the owner sets up a new number at no charge.' : `Owners will be charged ${dollars(cents)}.`}
          </p>
        </>
      )}
    </div>
  );
}

export default AdminSmsFeeSetting;
